import { useLayoutEffect } from "react";
import PropTypes from "prop-types";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import Wrapper from "../../helpers/Wrapper";

am5.addLicense("AM5C258896422");

const HorizontalClusteredBarChart = ({
    chartId,
    chartClass,
    chartData,
    yAxisField,
    seriesConfig
}) => {
    console.log('✌️chartData --->', chartData);
    useLayoutEffect(() => {
        const root = am5.Root.new(chartId);

        // Set themes
        root.setThemes([am5themes_Animated.new(root)]);
        root.interfaceColors.set("grid", am5.color(0xffffff));

        // Create chart
        var chart = root.container.children.push(am5xy.XYChart.new(root, {
            panX: false,
            panY: false,
            layout: root.verticalLayout,
            paddingRight: 0,
            paddingTop: 5,
            paddingBottom: 0,
            paddingLeft: 0,
            maxTooltipDistance: -1
        }));

        // Add cursor
        var cursor = chart.set("cursor", am5xy.XYCursor.new(root, {
            behavior: "zoomXY"
        }));
        cursor.lineY.set("visible", false);
        cursor.lineX.set("visible", false);

        // Define default color set
        var defaultColorSet = [0x11a1fd, 0xff9931, 0x5a75f9, 0x86a873, 0x28a745];

        // Create Y-axis (for categories)
        var yRenderer = am5xy.AxisRendererY.new(root, {
            stroke: am5.color(0x757575),
            strokeWidth: 0.3,
            strokeOpacity: 1,
            cellStartLocation: 0.1,
            cellEndLocation: 0.9
        });

        var yAxis = chart.yAxes.push(
            am5xy.CategoryAxis.new(root, {
                categoryField: yAxisField || "label",
                renderer: yRenderer
            })
        );

        // Configure Y-axis labels
        yAxis.get("renderer").labels.template.setAll({
            oversizedBehavior: "truncate",
            maxWidth: 150,
            textAlign: "right",
            fontSize: 12,
            fontFamily: "Ubuntu",
            fill: "#757575",
            fontWeight: "400"
        });

        yAxis.data.setAll(chartData);

        // Create axes map to store all value axes
        const axesMap = {};

        // Create primary X-axis (for values)
        var primaryXAxis = chart.xAxes.push(
            am5xy.ValueAxis.new(root, {
                renderer: am5xy.AxisRendererX.new(root, {
                    stroke: am5.color(0x757575),
                    strokeWidth: 0.3,
                    strokeOpacity: 1,
                    minGridDistance: 50
                }),
                min: 0
            })
        );

        // Store primary axis in the map
        axesMap.primary = primaryXAxis;

        // Create additional axes based on seriesConfig
        seriesConfig.forEach(config => {
            if (config.axisId && config.axisId !== 'primary' && !axesMap[config.axisId]) {
                // Create a new axis only if it doesn't exist yet
                const newAxis = chart.xAxes.push(
                    am5xy.ValueAxis.new(root, {
                        renderer: am5xy.AxisRendererX.new(root, {
                            stroke: am5.color(0x757575),
                            strokeWidth: 0.3,
                            strokeOpacity: 1,
                            minGridDistance: 50,
                            opposite: true
                        }),
                        min: config.axisMin !== undefined ? config.axisMin : 0,
                        max: config.axisMax !== undefined ? config.axisMax : undefined
                    })
                );

                // Configure X-axis labels
                newAxis.get("renderer").labels.template.setAll({
                    oversizedBehavior: "truncate",
                    maxWidth: 150,
                    textAlign: "center",
                    fontSize: 12,
                    fontFamily: "Ubuntu",
                    fill: "#757575",
                    fontWeight: "400"
                });

                // Store in axes map
                axesMap[config.axisId] = newAxis;
            }
        });

        // Configure primary X-axis labels
        primaryXAxis.get("renderer").labels.template.setAll({
            oversizedBehavior: "truncate",
            maxWidth: 150,
            textAlign: "center",
            fontSize: 12,
            fontFamily: "Ubuntu",
            fill: "#757575",
            fontWeight: "400"
        });

        // Format numbers
        root.numberFormatter.setAll({
            numberFormat: "#.##a",
            bigNumberPrefixes: [
                { number: 1e3, suffix: "K" },
                { number: 1e6, suffix: "M" },
                { number: 1e9, suffix: "B" },
            ],
            smallNumberPrefixes: []
        });

        // Add legend
        var legend = chart.children.push(
            am5.Legend.new(root, {
                position: "relative",
                x: am5.percent(50),
                centerX: am5.percent(50),
                marginTop: 15,
                useDefaultMarker: true,
                layout: am5.GridLayout.new(root, {
                    maxColumns: 10,
                    fixedWidthGrid: true
                })
            })
        );

        legend.markers.template.setAll({
            width: 13,
            height: 13
        });

        legend.markerRectangles.template.setAll({
            cornerRadiusTL: 20,
            cornerRadiusTR: 20,
            cornerRadiusBL: 20,
            cornerRadiusBR: 20
        });

        legend.labels.template.setAll({
            fontSize: 12,
            fontWeight: "400",
            fill: "#213345",
            oversizedBehavior: "wrap"
        });

        // Helper function for number formatting in tooltip
        function tooltipNumberFormatting(seriesName, value, unit) {
            if (value === undefined || value === null) return "N/A";

            const currencySymbol = unit || "$";

            // Format based on name/type if needed
            if (seriesName.toLowerCase().includes("premium") || seriesName.toLowerCase().includes("index")) {
                return value.toFixed(1);
            } else if (seriesName.toLowerCase().includes("value") || seriesName.toLowerCase().includes("sales")) {
                return currencySymbol + formatNumber(value);
            } else {
                return unit ? currencySymbol + formatNumber(value) : formatNumber(value);
            }
        }

        function formatNumber(value) {
            if (value >= 1000000) {
                return (value / 1000000).toFixed(1) + "M";
            } else if (value >= 1000) {
                return (value / 1000).toFixed(1) + "K";
            } else {
                return value.toFixed(1);
            }
        }

        // Store series references to access in tooltip
        var allSeries = [];

        // Create series function for horizontal bar chart
        function createSeries(config, index) {
            const {
                name,
                field,
                childLabel,
                childValue,
                childLabel2,
                childValue2,
                axisId = 'primary',
                color,
                unit
            } = config;

            // Get the axis to use
            const xAxisToUse = axesMap[axisId] || primaryXAxis;

            // Create the color as a proper am5.Color object
            var seriesColor = color ? am5.color(parseInt(color, 16)) : am5.color(defaultColorSet[index % defaultColorSet.length]);

            var series = chart.series.push(
                am5xy.ColumnSeries.new(root, {
                    name: name,
                    xAxis: xAxisToUse,
                    yAxis: yAxis,
                    valueXField: field,
                    categoryYField: yAxisField || "label",
                    childLabel: childLabel,
                    childValue: childValue,
                    childLabel2: childLabel2,
                    childValue2: childValue2,
                    unit: unit,
                    sequencedInterpolation: true,
                    clustered: true
                })
            );

            // Configure columns (horizontal bars)
            series.columns.template.setAll({
                fill: seriesColor,
                stroke: seriesColor,
                strokeWidth: 0,
                cornerRadiusTR: 7,
                cornerRadiusBR: 7
            });

            // Store color in the series for tooltip access
            series.set("tooltipColor", seriesColor);

            // Make columns height take 100% of available space
            series.columns.template.set("height", am5.p100);

            // Set up tooltip
            var tooltip = series.set(
                "tooltip",
                am5.Tooltip.new(root, {
                    getFillFromSprite: false,
                    getStrokeFromSprite: false,
                    autoTextColor: false,
                    pointerOrientation: "horizontal",
                    position: "pointer"
                })
            );

            tooltip.get("background").setAll({
                fill: am5.color(0x213345),
                cornerRadius: 5
            });

            tooltip.label.setAll({
                text: "[bold]{categoryY}[/]",
                fill: am5.color(0xffffff),
                fontSize: 13
            });

            // Store the series in our array for tooltip reference
            allSeries.push(series);

            // Custom tooltip text adapter that shows all series data
            tooltip.label.adapters.add("text", function (text, target) {
                var dataItem = target.dataItem;
                if (!dataItem) return text;

                var categoryY = dataItem.dataContext[yAxisField || "label"];
                text = "[bold]" + categoryY + "[/]\n";

                // Loop through all series to add their data
                allSeries.forEach(function (s) {
                    var valueField = s.get("valueXField");
                    var seriesName = s.get("name");
                    var seriesColor = s.get("tooltipColor");
                    var seriesUnit = s.get("unit");
                    var colorCode = seriesColor.toString();

                    // Add the series data with correctly colored bullet
                    text += "[" + colorCode + "]●[/] " +
                        "[bold]" + seriesName + ":[/] " +
                        "[bold]" + tooltipNumberFormatting(seriesName, dataItem.dataContext[valueField], seriesUnit) + "[/]\n";

                    // Add child values if they exist
                    var childLabel = s.get("childLabel");
                    var childValue = s.get("childValue");
                    var childLabel2 = s.get("childLabel2");
                    var childValue2 = s.get("childValue2");

                    if (childLabel && childValue && dataItem.dataContext[childValue] !== undefined) {
                        var secondaryColor = am5.color(defaultColorSet[5] || 0x757575);

                        text += "[" + secondaryColor.toString() + "]●[/] " +
                            "[bold]" + childLabel + ":[/] " +
                            "[bold]" + tooltipNumberFormatting(childLabel, dataItem.dataContext[childValue], seriesUnit) + "[/]\n";
                    }

                    if (childLabel2 && childValue2 && dataItem.dataContext[childValue2] !== undefined) {
                        var tertiaryColor = am5.color(defaultColorSet[6] || 0x333333);

                        text += "[" + tertiaryColor.toString() + "]●[/] " +
                            "[bold]" + childLabel2 + ":[/] " +
                            "[bold]" + tooltipNumberFormatting(childLabel2, dataItem.dataContext[childValue2], seriesUnit) + "[/]\n";
                    }
                });

                // Remove the extra newline at the end
                return text.slice(0, -1);
            });

            // Handle tooltip events - show the same tooltip for all series
            series.columns.template.events.on("pointerover", function (event) {
                var dataItem = event.target.dataItem;
                if (dataItem) {
                    // Update all tooltips with this data item
                    allSeries.forEach(function (s) {
                        s.set("tooltipDataItem", dataItem);
                    });

                    // Show tooltip
                    tooltip.show();
                }
            });

            series.columns.template.events.on("pointerout", function () {
                tooltip.hide();
            });

            // Set data
            series.data.setAll(chartData);
            series.appear();

            // Add to legend
            legend.data.push(series);

            return series;
        }

        // Create all series from the seriesConfig prop
        seriesConfig.forEach((config, index) => {
            createSeries(config, index);
        });

        chart.appear(1000, 100);

        return () => root.dispose();
    }, [chartId, chartData, yAxisField, seriesConfig]);

    return (
        <Wrapper>
            <div id={chartId} className={chartClass} style={{ height: "500px" }} />
        </Wrapper>
    );
};

HorizontalClusteredBarChart.propTypes = {
    chartId: PropTypes.string.isRequired,
    chartClass: PropTypes.string,
    chartData: PropTypes.arrayOf(PropTypes.object).isRequired,
    yAxisField: PropTypes.string.isRequired,
    seriesConfig: PropTypes.arrayOf(PropTypes.shape({
        field: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        unit: PropTypes.string,
        color: PropTypes.string,
        childLabel: PropTypes.string,
        childValue: PropTypes.string,
        childLabel2: PropTypes.string,
        childValue2: PropTypes.string,
        axisId: PropTypes.string,
        axisMin: PropTypes.number,
        axisMax: PropTypes.number
    })).isRequired
};

export default HorizontalClusteredBarChart;